import React, { useMemo, useState, useEffect } from "react";
import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import { useAdminStudents } from "../store/admin";
import {
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
} from "../components/AdminPageShell";

import StudentFilters from "./components/users/StudentFilters";
import StudentTable from "./components/users/StudentTable";
import StudentDetailsModal from "./components/users/StudentDetailsModal";
import SuspendConfirmModal from "./components/users/SuspendConfirmModal";
import { hoverActionStyles } from "./components/users/userUtils";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import "react-bootstrap";

// ===== MAIN: STUDENT MANAGEMENT PAGE =====
function UserManagement() {
  const {
    students,
    studentsLoading,
    studentsError,
    studentsTotal,
    studentsPages,
    fetchStudents,
    updateStudentStatus,
  } = useAdminStudents();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState("");
  const [sort, setSort] = useState("email");
  const [sortDir, setSortDir] = useState("asc");

  const [viewStudent, setViewStudent] = useState(null);
  const [suspendStudent, setSuspendStudent] = useState(null);
  const [toast, setToast] = useState(null);

  const PAGE_SIZE = 10;

  // GET /api/admin/students supports page, limit, search, status, emailVerified.
  // Filtering and pagination are server-side; sorting is applied to the
  // returned page because the contract has no sort parameter.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStudents({
        page,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
        status: statusFilter || undefined,
        emailVerified:
          emailVerifiedFilter === "verified"
            ? "true"
            : emailVerifiedFilter === "unverified"
              ? "false"
              : undefined,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [page, search, statusFilter, emailVerifiedFilter, fetchStudents]);

  const loadPage = (newPage) => {
    if (newPage < 1 || newPage > studentsPages) return;
    setPage(newPage);
  };

  const sortedStudents = useMemo(() => {
    const rows = [...students];
    rows.sort((a, b) => {
      const left =
        sort === "createdAt"
          ? new Date(a.createdAt || a.dateAdded || 0).getTime()
          : String(
              sort === "fullName"
                ? a.fullName || a.name || ""
                : a.email || ""
            ).toLowerCase();
      const right =
        sort === "createdAt"
          ? new Date(b.createdAt || b.dateAdded || 0).getTime()
          : String(
              sort === "fullName"
                ? b.fullName || b.name || ""
                : b.email || ""
            ).toLowerCase();
      if (left < right) return sortDir === "asc" ? -1 : 1;
      if (left > right) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [students, sort, sortDir]);

  const handleStatusConfirm = async (student, status) => {
    const res = await updateStudentStatus(student.id, status);
    if (res.success) {
      setSuspendStudent(null);
      setToast({
        message: `Student ${status === "SUSPENDED" ? "suspended" : "reactivated"} successfully`,
        isSuccess: true,
      });
      // No refetch needed — the store updates the student locally so the
      // list reflects the new status immediately without a "No students found" flash.
    } else {
      setToast({ message: res.error || "Failed to update status", isSuccess: false });
    }
  };

  const toggleSort = (field) => {
    if (sort === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(field); setSortDir("asc"); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <AdminNavBar
        title="Student Management"
        subTitle="Manage students, purchases, subscriptions and learning progress."
      />
      <AdminPageBody>
        <style>{hoverActionStyles}</style>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="mt-2 text-lg font-bold text-slate-900">Student Management</h4>
            <span className="text-[13px] text-slate-500">
              Manage students, purchases, subscriptions and learning progress.
            </span>
          </div>
        </div>

        {/* FILTERS */}
        <AdminSectionHeader
          title="Students"
          count={studentsTotal}
          description="View and manage all registered students."
        />

        <StudentFilters
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          statusFilter={statusFilter}
          onStatusFilterChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          emailVerifiedFilter={emailVerifiedFilter}
          onEmailVerifiedFilterChange={(value) => {
            setEmailVerifiedFilter(value);
            setPage(1);
          }}
          sort={sort}
          onSortChange={setSort}
        />

        {/* TABLE */}
        <AdminTablePanel>
          {studentsLoading ? (
            <AdminLoadingState />
          ) : studentsError ? (
            <div className="p-6 text-sm text-red-600">{studentsError}</div>
          ) : sortedStudents.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              <div className="mb-2.5 text-3xl">🎓</div>
              No students found. Adjust your filters or add a student.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <StudentTable
              students={sortedStudents}
              loading={studentsLoading}
              error={studentsError}
              onView={setViewStudent}
              onSuspend={setSuspendStudent}
              sort={sort}
              sortDir={sortDir}
              toggleSort={toggleSort}
            />
            </div>
          )}
        </AdminTablePanel>

        {/* PAGINATION */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2.5">
          <span className="text-[13px] text-slate-500">
            Showing {sortedStudents.length} of {studentsTotal} students
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => loadPage(page - 1)}
              disabled={page <= 1}
              className={`rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium ${
                page <= 1 ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-slate-50"
              }`}
            >Prev</button>
            <span className="text-[13px] font-medium text-slate-700">Page {page} of {studentsPages}</span>
            <button
              onClick={() => loadPage(page + 1)}
              disabled={page >= studentsPages}
              className={`rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium ${
                page >= studentsPages ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-slate-50"
              }`}
            >Next</button>
          </div>
        </div>

        {/* MODALS */}
        <StudentDetailsModal
          isOpen={!!viewStudent}
          onClose={() => setViewStudent(null)}
          student={viewStudent}
        />
        <SuspendConfirmModal
          isOpen={!!suspendStudent}
          onClose={() => setSuspendStudent(null)}
          student={suspendStudent}
          onConfirm={handleStatusConfirm}
        />
      </AdminPageBody>

      {toast && (
        <Toast
          message={toast.message}
          isSuccess={toast.isSuccess !== false}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default UserManagement;